/* OnSumn — Map components: MapView, MapEventCard, EventsMapSection
   Extracted from index.html. Behavior unchanged. */

/* City display + city equality come from ONE authority (lib/city-registry.js).
   normalizeCityKey in particular is not optional here: the feed filter below
   used to compare `e.city === activeCity` raw, which silently empties the
   whole feed the moment the two sides disagree on casing — a filter that
   returns nothing looks exactly like "the Bay is quiet tonight." */
import { normalizeCityKey, cityInk } from '../lib/city-registry.js';
/* Venue entry rules — the map card is the FIFTH client surface on the ONE
   gate (Events.jsx card, VenueEventList rows, SearchDetailPane, VenueHeader
   poster). Michael 2026-08-20: "add it to the map cards too" — this
   supersedes the same-day audit note that left the map card chip-free. */
import {
  policyChipForEvent, useVenuePolicies,
  rulesChipStyle, rulesChipSquareStyle, rulesChipSquareHidden,
  rulesChipAria, rulesChipTitle, rulesChipLabel, chipKeyDownGuard,
} from '../lib/venue-policies-gate.js';
/* Seasonal rail — the roster (zero imports) + the Bay's calendar day.
   activeOccasions / occasionWindow / occasionAnchor are pure;
   pacificTodayIso is the ONE Pacific-day formatter the hubs already read,
   so the homepage rail and /<slug> agree on which day it is. */
import { activeOccasions, occasionAnchor, occasionWindow } from '../lib/occasions.js';
import { pacificTodayIso } from '../lib/pacific-day.js';

/* ── Goal A helpers — meaningful-content gate + plaintext desc (R5).
   stripHtmlToText: <br> variants → space, strip remaining tags, collapse
   whitespace, trim. The stripped string is rendered as a plain React TEXT
   node — never an HTML sink (ingestion strips URLs, not script / img-onerror
   payloads, so one sink would be stored XSS on the map). ── */
function stripHtmlToText(s) {
  if (typeof s !== 'string' || !s) return '';
  return s
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
function normalizeForCompare(s) { return stripHtmlToText(s).toLowerCase(); }

/* ───── Map Event Card (slides up when a pin is clicked) ───── */
function MapEventCard({ event, darkMode, radius, onClose, position, onCategoryClick, feedGenerated }) {
  // Goal A expand state. Hooks sit ABOVE the null guard: MapView renders this
  // component with event=null until a pin is tapped, and React requires a
  // stable hook count across those renders. Expanded is per-card and RESETS
  // when a new pin opens (R6) — keyed on event identity rather than a remount
  // at the render site, so the mapCardPop entrance still fires only on open
  // (null → event), exactly as today.
  const [expanded, setExpanded] = React.useState(false);
  const eventKey = event ? (event.slug || event.id || event.title || '') : null;
  // ENTRY rules trigger rect — hooks above the guard for the same stable-
  // hook-count reason as `expanded`. Reset joins the per-pin effect (R6):
  // an open rules popover must not survive a pin switch.
  const [rulesTriggerRect, setRulesTriggerRect] = React.useState(null);
  const venuePolicies = useVenuePolicies();
  React.useEffect(() => { setExpanded(false); setRulesTriggerRect(null); }, [eventKey]);
  const reducedMotion = (typeof window !== 'undefined' && window.useReducedMotion)
    ? window.useReducedMotion() : false;
  if (!event || !position) return null;
  // null = no chip (absent, not disabled) — the shared spec §1 contract.
  const rulesPolicy = policyChipForEvent(venuePolicies, event);
  // Chip color comes from CATEGORY, not city. Pre-fix the chip background
  // used tagColor (city) so a MUSIC event in Oakland rendered amber, in SF
  // orange, in San Jose deep eucalyptus — the chip text said MUSIC but the
  // color meant "where," which read as palette drift. Now MUSIC is always
  // intl orange, ART always eucalyptus, etc. — matches lib/category-colors.js.
  const CAT_COLORS = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
    'MUSIC': '#FF4F00', 'ART': '#5A7A68', 'FOOD': '#D57800',
    'NIGHTLIFE': '#7C5BBC', 'CARDS': '#7A99AC', 'COMMUNITY': '#395542',
  };
  // Was `|| tagColor` — a city hue standing in for an unknown category, the
  // same "colour means where" drift the comment above says was already fixed
  // once. Now it falls back within the CATEGORY family, matching Events.jsx.
  const catColor = CAT_COLORS[event.category] || 'var(--international-orange)';
  /* Meta-line ink. The hand-kept META_INK table that stood here — 12 entries,
     each a dark/light pair, plus three more added by hand the moment three new
     city tags appeared in the live feed — is replaced by lib/city-registry
     #cityInk. That table was the strongest possible evidence that per-city
     colour does not scale: it needed an Iris contrast round every time a
     scraper found a new municipality, and it still ended in a fallback that
     inked BERKELEY, CONCORD and FREMONT in SF's exact hue. cityInk keeps the
     dual-mode discipline the table established (a flat brand hex cannot clear
     AA in both themes) and applies it to two declared anchors plus one neutral
     — a set that cannot go stale as the city vocabulary grows. */
  const metaColor = cityInk(event.tag, darkMode);
  const bgHue = { MUSIC: 30, ART: 340, FOOD: 25, NIGHTLIFE: 260, COMMUNITY: 200, CARDS: 220 }[event.category] || 25;
  const cardW = 240, cardH = 220;
  const cardLeft = Math.max(6, Math.min(position.x - cardW / 2, position.containerW - cardW - 6));
  const cardTop = Math.max(6, position.y - cardH - 14);

  // ── Goal A: meaningful-content gate (R5) ──
  // ~72% of live descs are literal echoes of the title and ~6% of events carry
  // neither desc nor subgenres — the affordance renders ONLY when the expand
  // would reveal something real: a desc that differs from the title (HTML-
  // stripped compare) or subgenre chips. Echo/empty payloads stay compact.
  const descText = stripHtmlToText(event.desc);
  const hasMeaningfulDesc = !!descText
    && normalizeForCompare(event.desc) !== normalizeForCompare(event.title || '');
  const subgenres = Array.isArray(event.subgenres)
    ? event.subgenres.filter((s) => typeof s === 'string' && s.trim()) : [];
  const meaningful = hasMeaningfulDesc || subgenres.length > 0;

  // ── Goal A: tonight-pill predicate — #101 registry-hybrid (controller
  // ruling 2026-07-21, amending the Goal A brief's R2 mechanism letter; the
  // intent is preserved and tightened). The pill TEXT comes ONLY from the
  // lib/loss-copy.js registry via window.lossCopy('tonight-badge', event,
  // feedGenerated) — never a literal here — so the loss-framing gate's kill
  // switch, 24h feed-freshness ceiling, and expiresAt fail-closed rules
  // protect this surface. Those SHARED lossCopy legs match the grid badge
  // (Events.jsx date-proximity render; feedGenerated is the same
  // useEvents().generated stamp, threaded from EventsMapSection). The
  // alreadyPast leg below is MAP-ONLY — an adjudicated divergence
  // (2026-07-21, Mav ruling: the map errs toward suppression; the grid
  // stays date-only pending a fast-follow). Do NOT "fix" either surface to
  // match the other. Composed legs, ALL required:
  //   (a) lossCopy returns the badge text (date signal + fresh feed + switch)
  //   (b) FlowsFeedFormat.isEventToday(event) — the brief's letter; reads
  //       event.date, never iso_date (venue-spine-only historically)
  //   (c) NOT already-past via the injectable Pacific seam — never
  //       new Date().getHours() (the recurring "3 AM" bug). today + TBD/
  //       unparseable start still shows: the date claim is provable, the
  //       past claim is not (flows' reachable-tonight precedent).
  // Any leg fails, or either helper is absent from window → no pill, no throw.
  const FFF = (typeof window !== 'undefined' && window.FlowsFeedFormat) || null;
  let tonightText = null;
  if (FFF && typeof FFF.isEventToday === 'function'
    && typeof window !== 'undefined' && typeof window.lossCopy === 'function') {
    try {
      const badgeText = window.lossCopy('tonight-badge', event, feedGenerated);
      if (badgeText && FFF.isEventToday(event)) {
        const startMin = (typeof FFF.parseEventStartMinutes === 'function')
          ? FFF.parseEventStartMinutes(event.time) : null;
        // Strict form (Atlas co-sign, 2026-07-21): a CONCRETE start with no
        // now-seam on the page fails CLOSED (suppress) — a temporal guard
        // that fails open re-plants the recurring "3 AM" seed. A null
        // startMin (TBD/unparseable) is unaffected → pill still shows.
        const alreadyPast = startMin != null
          && (typeof FFF.nowMinutesPacific !== 'function' || startMin < FFF.nowMinutesPacific());
        if (!alreadyPast) tonightText = badgeText;
      }
    } catch (e) { tonightText = null; }
  }

  // ── Goal A: bottom clamp (R3) ──
  // The expanded card grows DOWNWARD from the unchanged compact top. jsdom
  // cannot gate layout, so this is stated, not unit-tested: the revealed
  // region's height budget = container bottom − card top − the compact card's
  // own footprint (~264px measured, incl. the affordance row), floored and
  // capped, with overflow scrolling past the budget. Runtime-verified after.
  const containerH = (typeof position.containerH === 'number') ? position.containerH : 480;
  const detailMaxH = Math.max(72, Math.min(240, containerH - cardTop - 6 - 264));

  return (
    <div style={{
      position: 'absolute',
      left: cardLeft,
      top: cardTop,
      // Chrome tier per memory/reference_zindex_hierarchy.md (chrome=1000s,
      // dialogs 1500+). Must clear Leaflet's markerPane (z 600 — the old 600
      // here TIED it and lost, so opaque cluster badges painted over the
      // revealed desc), popupPane (700), and Leaflet controls (1000).
      zIndex: 1001,
      width: cardW,
      animation: 'mapCardPop 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    }}>
      <div style={{
        /* Near-solid shell (Zora spec 2026-07-21): the labeled basemap put
           street text directly under the card, and 0.45 glass let it read
           through wherever backdrop-filter doesn't render. The card is a
           READING surface — 0.96 fill carries legibility on its own; the
           blur below stays as progressive enhancement (provably irrelevant
           at 4% see-through). Locked by the alpha ≥ 0.9 gate in
           tests/map-event-card.test.js. */
        background: darkMode ? 'rgba(18,20,20,0.96)' : 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        /* Rim compensation for the solid fill: firmer border both modes;
           dark keeps a top-edge-only inset highlight (replaces the old
           uniform 0.5px inset), light drops the inset entirely (white-on-
           white is invisible — the border carries the rim, matching
           SearchPanel's solid-sheet treatment). */
        border: darkMode ? '1px solid rgba(255,255,255,0.16)' : '1px solid rgba(0,0,0,0.14)',
        borderRadius: Math.max(radius - 4, 10),
        overflow: 'hidden',
        boxShadow: darkMode
          ? '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12)'
          : '0 8px 32px rgba(0,0,0,0.22)',
      }}>
        {/* Close — 44×44 hit area, 24px visible chip (WCAG 2.5.8 / Iris floor) */}
        <button onClick={onClose} aria-label="Close event card" style={{
          position: 'absolute', top: 0, right: 0, zIndex: 10,
          width: 44, height: 44, borderRadius: 0,
          background: 'transparent', border: 'none',
          padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span aria-hidden="true" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, borderRadius: '50%',
            background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 12, lineHeight: 1,
          }}>✕</span>
        </button>
        {/* Image */}
        <div style={{
          height: 90, position: 'relative', overflow: 'hidden',
          background: `oklch(${darkMode ? 0.3 : 0.45} 0.06 ${bgHue})`,
        }}>
          {/* Map popup thumbnail. CF resize at ~600w covers the popup
           * @2x on mobile (popup is ~300px wide max). No srcset — popup
           * size is fixed. Raw-URL onError fallback for the same reason
           * as Events.jsx — DoTheBay headers can confuse the resizer. */}
          {event.image && <img
            src={typeof window !== 'undefined' && typeof window.cfImage === 'function'
              ? window.cfImage(event.image, { width: 600 })
              : event.image}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const el = e.currentTarget;
              if (el.dataset.cfFallback === 'raw') return;
              if (event.image && el.src !== event.image) {
                el.dataset.cfFallback = 'raw';
                el.src = event.image;
              }
            }}
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
            }}
          />}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)',
          }} />
          {/* Category pill — clickable when the parent provides a handler.
              Opens the Filter Popup pre-selected for that category so users
              can pivot from "I tapped this thing" to "show me everything
              like this thing." Falls back to display-only div if no handler. */}
          {typeof onCategoryClick === 'function' ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCategoryClick(event.category); }}
              aria-label={`Filter to ${event.category} events`}
              className="focus-double-ring"
              style={{
                position: 'absolute', top: 6, left: 6,
                background: catColor,
                color: (typeof window !== 'undefined' && window.categoryTextColor)
                  ? window.categoryTextColor(catColor) : '#10110F',
                fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: 100,
                border: 'none', cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.boxShadow = `0 2px 8px ${catColor}55`; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
            >{event.category}</button>
          ) : (
            /* Display-only twin of the button above. It was still painting
               itself with the CITY colour while the clickable branch used the
               category colour — the same chip, two meanings, depending only on
               whether a handler happened to be passed. Both are catColor now. */
            <div style={{
              position: 'absolute', top: 6, left: 6,
              background: catColor,
              color: (typeof window !== 'undefined' && window.categoryTextColor)
                ? window.categoryTextColor(catColor) : '#10110F',
              fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '4px 10px', borderRadius: 100,
            }}>{event.category}</div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '8px 10px 10px' }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: metaColor, marginBottom: 2,
          }}>
            {/* tonight pill — leading the line, text from the #101 registry.
                Temporal-glass grammar (Zora ruling, #101 condition 4): a
                temporal FACT wears the dark-glass family — never a category
                color, and never international orange, which is hex-identical
                to the MUSIC chip two rows up. Theme-invariant dark scrim
                (0.68→0.58, the grid proximity-badge model) keeps the white
                ink ≥5:1 in both modes. Static fact-badge: no motion.
                Deliberately PERSISTS into the expanded state — a deviation
                from FIX 3's "in COMPACT" letter, adjudicated Zora+Mav
                2026-07-21 (a freshness signal must not vanish mid-read). */}
            {tonightText && (
              <span data-loss-copy="tonight-badge" style={{
                display: 'inline-block', verticalAlign: 'middle',
                background: 'linear-gradient(135deg, rgba(0,0,0,0.68), rgba(0,0,0,0.58))',
                border: '1px solid rgba(255,255,255,0.18)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 8px rgba(0,0,0,0.3)',
                color: '#FFFFFF',
                fontSize: 8, fontWeight: 700, letterSpacing: '0.1em',
                padding: '2px 7px', borderRadius: 100, marginRight: 6,
                pointerEvents: 'none',
              }}>{tonightText}</span>
            )}
            {/* "TAG · DATE · TIME" is built by JOIN, not by literal
                separators. With the city authority refusing ~30% of venues,
                a literal `{event.tag} · {event.date}` renders a leading
                orphan " · " that reads as a missing word — and an empty slot
                where a locality belongs invites the reader to supply one.
                Absent parts drop out with their separator. */}
            {[event.tag, event.date, event.time].filter(Boolean).join(' · ')}
          </div>

          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700,
            color: darkMode ? '#fff' : '#1a1a1a',
            lineHeight: 1.2, marginBottom: 3,
            // Expanded kills the one-line title lock so the full title wraps;
            // compact keeps today's single-line ellipsis exactly.
            ...(expanded
              ? { overflowWrap: 'break-word' }
              : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
          }}>{event.title}</div>

          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 10,
            /* Zora spec: 0.5 white = 6.18:1 on the solid dark shell;
               #6B6B6B = 5.33:1 on the light shell (old 0.4/#888 fell
               under AA at 10px). */
            color: darkMode ? 'rgba(255,255,255,0.5)' : '#6B6B6B',
            marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {(() => {
              const _slug = (typeof findVenueSlug === 'function') ? findVenueSlug(event.venue) : null;
              return _slug ? (
                <a
                  href={`/venue/${_slug}`}
                  className="venue-link"
                  aria-label={`See all upcoming shows at ${event.venue}`}
                  style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted currentColor' }}
                  onClick={(e) => e.stopPropagation()}
                >{event.venue}</a>
              ) : event.venue;
            })()}
          </div>

          {/* Goal A — tap-to-expand via an explicit visible affordance (a real
              button, never an invisible full-card hitbox). Collapse fires ONLY
              here or on Close — body taps stay inert, so a stray tap can never
              sit under the I'M SLIDING CTA below. Renders ONLY for meaningful
              content (desc ≠ title after an HTML-strip, or subgenres): on
              echo/empty payloads the card stays compact with no affordance. */}
          {meaningful && (
            <button
              type="button"
              className="focus-double-ring"
              aria-expanded={expanded}
              aria-controls="map-card-details"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                width: '100%', minHeight: 44, padding: '4px 0', margin: '0 0 2px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                // Iris co-sign 2026-07-21: 0.85/0.72 — the old 0.55 light
                // value dipped to ~4.4:1 with warm cluster bleed behind glass.
                color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.72)',
              }}
            >
              <span>{expanded ? 'LESS' : 'MORE'}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: reducedMotion ? 'none' : 'transform 0.18s ease',
                }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          )}
          {/* Revealed region — desc as HTML-stripped plain TEXT, subgenre
              chips, PUT-ON provenance. Content mounts only while expanded
              (honest "gone" on collapse); the 0fr→1fr grid row is the CSS
              height transition, snapped by reduced-motion. The wrapper stays
              mounted so aria-controls always resolves. */}
          {meaningful && (
            <div id="map-card-details" style={{
              display: 'grid',
              gridTemplateRows: expanded ? '1fr' : '0fr',
              transition: reducedMotion ? 'none' : 'grid-template-rows 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                {expanded && (
                  <div
                    // Iris co-sign 2026-07-21: focusable named region so
                    // keyboard-only users can scroll an overflowing desc
                    // (Firefox/Safari don't focus scroll containers).
                    tabIndex={0}
                    role="region"
                    aria-label="Event details"
                    style={{ maxHeight: detailMaxH, overflowY: 'auto', marginBottom: 6 }}
                  >
                    {hasMeaningfulDesc && (
                      <div style={{
                        fontFamily: 'var(--font-body)', fontSize: 11, lineHeight: 1.45,
                        color: darkMode ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.72)',
                        overflowWrap: 'break-word',
                        marginBottom: (subgenres.length > 0 || event.provenance === 'user') ? 7 : 2,
                      }}>{descText}</div>
                    )}
                    {subgenres.length > 0 && (
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 4,
                        marginBottom: event.provenance === 'user' ? 7 : 2,
                      }}>
                        {subgenres.map((sg) => (
                          <span key={sg} style={{
                            fontFamily: 'var(--font-body)', fontSize: 8.5, fontWeight: 700,
                            letterSpacing: '0.06em', textTransform: 'uppercase',
                            padding: '2px 7px', borderRadius: 100,
                            border: darkMode ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(0,0,0,0.18)',
                            color: darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
                          }}>{sg}</span>
                        ))}
                      </div>
                    )}
                    {/* PUT-ON — user-submitted provenance, modeled on Events.jsx
                        PutOnBadge (not window-attached there, so inlined). */}
                    {event.provenance === 'user' && (
                      <div
                        role="img"
                        aria-label="Community-submitted event"
                        title={event.handle ? ('Added by @' + event.handle + ' via flyer') : 'Added via flyer drop'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          height: 20, padding: '0 7px 0 5px',
                          borderRadius: 6, marginBottom: 2,
                          background: darkMode ? '#395542' : '#E3EDE6',
                          border: darkMode ? '1px solid #5A7A68' : '1px solid #395542',
                          color: darkMode ? '#DDEEE3' : '#395542',
                          fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 800,
                          letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1,
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 48 48" fill="currentColor" aria-hidden="true">
                          <path d="M24 4 L26 22 L44 24 L26 26 L24 44 L22 26 L4 24 L22 22 Z" />
                        </svg>
                        <span>PUT-ON</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Social-proof + share row — chip + 44x44 share button. Hidden
              counts (<3) collapse the chip so a one-share popup still feels
              tight. Both children stopPropagation so a stray tap doesn't
              spoof the I'M SLIDING button below. */}
          {(event.slug || rulesPolicy) && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, marginBottom: 6, minHeight: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {event.slug && typeof SlidingChip === 'function' && (
                  <SlidingChip slug={event.slug} darkMode={darkMode} />
                )}
                {/* ENTRY chip — venue rules + bag policy, in the always-
                    visible social row (never behind the MORE expander: rules
                    availability must not depend on desc quality). Same fog
                    pill + gate + popover as the grid card; stopPropagation
                    per this row's discipline (the I'M SLIDING CTA sits
                    below). Fallback when RulesPopover hasn't loaded: the
                    venue's own policy page. */}
                {rulesPolicy && (
                  <button
                    type="button"
                    className="focus-double-ring"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof window.RulesPopover === 'function') {
                        setRulesTriggerRect(e.currentTarget.getBoundingClientRect());
                      } else {
                        window.open(rulesPolicy.entry.source_url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    onKeyDown={chipKeyDownGuard}
                    aria-label={rulesChipAria(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
                    aria-haspopup="dialog"
                    title={rulesChipTitle(rulesChipLabel(rulesPolicy, event), rulesPolicy.entry.source_kind)}
                    style={rulesChipStyle(darkMode)}
                  >
                    {!rulesChipSquareHidden() && (
                      <span aria-hidden="true" style={rulesChipSquareStyle(darkMode, rulesPolicy.state)} />
                    )}
                    ENTRY
                  </button>
                )}
              </div>
              {event.slug && typeof ShareIconButton === 'function' && (
                <ShareIconButton event={event} darkMode={darkMode} />
              )}
            </div>
          )}
          {rulesTriggerRect && rulesPolicy && typeof window !== 'undefined' && typeof window.RulesPopover === 'function' && (
            <window.RulesPopover
              event={event}
              entry={rulesPolicy.entry}
              state={rulesPolicy.state}
              eventRules={rulesPolicy.eventRules}
              darkMode={darkMode}
              triggerRect={rulesTriggerRect}
              onClose={() => setRulesTriggerRect(null)}
            />
          )}

          <button
            onClick={() => {
              // Sliding ritual: mark interest BEFORE opening the link so the
              // INTEREST KV increment fires whether or not window.open succeeds
              // (popup blockers, no-link events, etc.). markInterested is a
              // no-op after the first tap per-slug (localStorage gate), so
              // double-clicks won't double-count. Popup has no room for a 250ms
              // pulse — fire mark + open in the same tick, matching the
              // unpulse'd repeat-tap path of EventCard's sliding ritual.
              //
              // Destination: external ticket link when present + http(s);
              // otherwise the OnSumn /e/[slug] preview so user-submitted
              // events (which carry no event.link) still navigate somewhere
              // instead of silently no-op'ing.
              if (typeof markInterested === 'function') markInterested(event.slug);
              const isHttp = event.link && /^https?:\/\//i.test(event.link);
              const externalUrl = isHttp ? event.link : null;
              const fallbackUrl = event.slug ? `/e/${encodeURIComponent(event.slug)}` : null;
              const url = externalUrl || fallbackUrl;
              if (url) window.open(url, '_blank', 'noopener,noreferrer');
            }}
            style={{
              width: '100%', padding: '7px 0',
              background: 'var(--international-orange)',
              color: 'var(--on-international-orange)', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              borderRadius: Math.max(radius - 8, 6),
              transition: 'transform 0.15s, box-shadow 0.15s',
              boxShadow: '0 2px 10px rgba(255,79,0,0.25)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(255,79,0,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(255,79,0,0.25)'; }}
          >I'M SLIDING →</button>
        </div>
      </div>
    </div>
  );
}

/* ───── Goal B constants — search + locate (docs/design/2026-07-20-fable5-
   map-search-prompt.md + .superpowers/sdd/goalB-addendum.md) ───── */
const SEARCH_FLY_ZOOM = 14;   // landing zoom for a named search hit
const LOCATE_FLY_ZOOM = 13;   // brief FIX 3: m.flyTo(user, ~13)
const FLY_EPSILON_DEG = 1e-4; // ~11 m — "already there" for zero-delta opens
// Locate copy — Rhea-approved verbatim (co-sign 2026-07-21). The denial chip
// is VISIBLE (brief FIX 3; R5: aria-live polite, near the control); finding/
// centered are visually-hidden announcements in the SAME live region. All
// three are STATIC strings — never coordinates (Atlas fence).
const LOCATE_DENIED_COPY = "Location's off in your browser — flip it on to center the map on you.";
const LOCATE_FINDING_COPY = 'Finding your location…';
const LOCATE_CENTERED_COPY = 'Centered on your location.';

/* ───────────────────────────────────────────────────────────────────────────
   Self-contained basemap (2026-08-26) — the /flows P1 pattern on the pulse
   map. CARTO began watermarking keyless tile traffic ("API KEY REQUIRED"
   diagonals, verified on prod), so the default ground is now the committed
   Natural Earth land artifact (data/bay-context.geojson, public domain — see
   its meta + the generator scrapers/assets/make-bay-context.js) drawn over a
   water-colored container. 'carto' keeps the LABELED tile path (Goal B
   FIX 4: *_all city names) intact so a future keyed CARTO account can
   re-enable street labels without surgery:
     window.MAP_BASEMAP = 'carto'   (set before mount; anything else → self)
   Trade acknowledged: 'self' renders no city/street labels — a coastline
   with pins beats a labeled map wearing a watermark, and search still flies
   to named results. The context layer is GROUND, never figure: pins and
   clusters keep exactly their pre-change prominence.
   ─────────────────────────────────────────────────────────────────────── */
const MAP_BASEMAP_DEFAULT = 'self';
function mapBasemapMode() {
  const v = typeof window !== 'undefined' ? window.MAP_BASEMAP : null;
  return v === 'carto' || v === 'self' ? v : MAP_BASEMAP_DEFAULT;
}

// Dedicated pane for the ground: above tilePane (200), below the vector
// overlayPane (400) and markerPane (600) — land can never paint over pins.
const MAP_GROUND_PANE = 'map-ground';

// [ [S,W], [N,E] ] — mirrors data/bay-context.geojson meta.bbox (the artifact
// is the source of truth; tests/map-basemap-self.test.js pins this mirror
// against it, and flows carries the identical constant). In 'self' mode the
// map clamps here so the frame never pans into void beyond the committed
// geography.
const MAP_CONTEXT_BBOX_LATLNG = [[36.75, -123.15], [38.45, -121.35]];

// Ground tokens — the flows LENS ground values verbatim (one Bay, one water,
// both surfaces agree): dark water == --surface-container-lowest (#0C0F0F),
// land ~4.5 lightness points up — a subtle luminance delta, never a competing
// surface — with a hairline coastline. Light keeps the same polarity (water
// always the darker of the pair, fog-blue cool cast).
const MAP_GROUND = {
  dark: { water: '#0C0F0F', land: '#161A19', coast: 'rgba(255,255,255,0.10)' },
  light: { water: '#E2E7E4', land: '#F5F2EC', coast: 'rgba(26,23,20,0.18)' },
};

/* ───── Map View (Leaflet, uses glass tokens) ───── */
function MapView({ events, darkMode, height = 480, radius, onCategoryClick, feedGenerated, baseGeo }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const layerRef = React.useRef(null);
  const tileRef = React.useRef(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [cardPos, setCardPos] = React.useState(null);
  // Leaflet code-split (Issue #44): lib/leaflet-loader.js injects script
  // + CSS tags on demand. `leafletReady` flips to true once the global L
  // is initialized — both Leaflet itself and the MarkerCluster plugin.
  // `loadError` carries the error if injection fails (offline / CDN
  // outage / SRI mismatch).
  const [leafletReady, setLeafletReady] = React.useState(
    () => typeof window !== 'undefined'
      && typeof window.isLeafletReady === 'function'
      && window.isLeafletReady()
  );
  const [loadError, setLoadError] = React.useState(null);
  // ── Goal B state — search fly-to + fenced locate ──
  // locateState carries ONLY a status kind ('finding' | 'centered' |
  // 'denied'); user coordinates are NEVER stored (they feed one camera move
  // inside the geolocation callback and die there).
  const [locateState, setLocateState] = React.useState(null);
  const searchMarkersRef = React.useRef({}); // slug/id → Leaflet marker (cluster resolution)
  const flySeqRef = React.useRef(0);         // supersedes stale moveend/zoomToShowLayer opens
  const pendingMoveendRef = React.useRef(null);
  // Reduced-motion (Iris co-sign): both Goal B camera moves snap via
  // m.setView instead of flying. Presence-conditional per the house pattern
  // (MapEventCard above) — the hook's availability is static per page load,
  // so the hook order stays stable.
  const reducedMotion = (typeof window !== 'undefined' && window.useReducedMotion)
    ? window.useReducedMotion() : false;
  // Zora co-sign: the card must never paint over an open search panel. The
  // marker-click handler is a KEEP range, so exclusivity lives OUTSIDE it:
  // any card open (any path) bumps the epoch, remounting the map SearchBar
  // closed. Search-select opens already closed the panel via onClose — the
  // bump is a no-op there.
  const [searchEpoch, setSearchEpoch] = React.useState(0);
  // Iris re-confirm (WCAG 2.4.3): the epoch remount destroys the trigger the
  // select handlers had focused, so a SEARCH-SELECT open moves focus INTO
  // the card — its Close button — from here (MapEventCard's bytes are A's
  // KEEP range; it is focused from outside, never edited). reveal() sets the
  // flag in the same commit that renders the card, and this effect runs
  // after that commit, so the button exists and takes focus BEFORE the
  // epoch-bump re-render tears the (now unfocused) trigger down. Marker
  // clicks never set the flag — an unsolicited open must not yank focus.
  const searchOpenFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (selectedEvent) setSearchEpoch((n) => n + 1);
    if (selectedEvent && searchOpenFocusRef.current) {
      searchOpenFocusRef.current = false;
      const rootEl = ref.current ? ref.current.parentElement : null;
      const closeBtn = rootEl ? rootEl.querySelector('button[aria-label="Close event card"]') : null;
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }
  }, [selectedEvent]);

  // Kick off the dynamic load when the component mounts. Idempotent —
  // the loader memoizes its promise so repeated mounts share one fetch.
  React.useEffect(() => {
    if (leafletReady) return;
    if (typeof window === 'undefined' || typeof window.loadLeaflet !== 'function') {
      // Loader script not on page yet — try again on next tick.
      const t = setTimeout(() => {
        if (typeof window.loadLeaflet === 'function') {
          window.loadLeaflet()
            .then(() => setLeafletReady(true))
            .catch((err) => setLoadError(err));
        } else {
          setLoadError(new Error('leaflet-loader not available'));
        }
      }, 50);
      return () => clearTimeout(t);
    }
    let cancelled = false;
    window.loadLeaflet()
      .then(() => { if (!cancelled) setLeafletReady(true); })
      .catch((err) => { if (!cancelled) setLoadError(err); });
    return () => { cancelled = true; };
  }, [leafletReady]);

  // Batch-hydrate interest counts for everything pinned on the map.
  // Map can render hundreds of pins — fetchInterestCounts skips already-cached
  // slugs (60s localStorage TTL) so this stays one /api/interest GET per session.
  const slugList = React.useMemo(
    () => (events || []).map(e => e.slug).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(events || []).map(e => e.slug).join('|')]
  );
  if (typeof useHydrateInterest === 'function') useHydrateInterest(slugList);

  React.useEffect(() => {
    if (!ref.current || !leafletReady || !window.L) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const basemap = mapBasemapMode();
    const m = window.L.map(ref.current, {
      center: [37.76, -122.20],
      zoom: 10,
      scrollWheelZoom: window.innerWidth > 768,
      // The tile layer used to own the zoom ceiling; the map owns it now so
      // both basemap modes behave identically.
      maxZoom: 19,
      // 'self' renders committed geography that ends at MAP_CONTEXT_BBOX —
      // clamp pan/zoom to the artifact so the frame never shows void past
      // the data. (Locate taps from outside the Bay land on the Bay edge
      // instead of a blank ocean of nothing — the site is Bay-only anyway.)
      ...(basemap === 'self'
        ? { maxBounds: MAP_CONTEXT_BBOX_LATLNG, maxBoundsViscosity: 1.0, minZoom: 8 }
        : {}),
    });
    if (basemap === 'carto') {
      // Goal B FIX 4: LABELED CARTO variants (*_all) — the Phase-1 default was
      // *_nolabels, which renders zero city names, so "search the Bay" had no
      // geography to read. Same provider + attribution + license (carto-osm);
      // never a custom label layer. Legibility in both themes is a visual
      // co-sign check (Zora/Iris).
      const tileUrl = darkMode
        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
      // C1 (2026-07-16): the OSM/CARTO credit is LICENSE-REQUIRED (ODbL + CARTO
      // terms — lib/licensed-sources.js 'carto-osm') WHENEVER these tiles
      // render. attributionControl was suppressed here once; never suppress it
      // again — restyle it below instead. The guard
      // (tests/attribution-sources.test.js) fails the build if this string
      // leaves the bundle. 2026-08-26: tiles + credit now live together behind
      // this branch — in 'self' mode NEITHER renders, because crediting
      // CARTO/OSM for geography they did not draw would be a false attribution
      // (tests/map-basemap-self.test.js pins both directions).
      tileRef.current = window.L.tileLayer(tileUrl, {
        maxZoom: 19,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
      }).addTo(m);
    } else {
      // 'self' (default): zero third-party runtime dependency. The ground
      // pane holds committed Natural Earth land (public domain — no credit
      // owed, none rendered; see data/bay-context.geojson meta.license) drawn
      // by the effect below; water is the container background. BELOW the
      // tile pane (200) since 2026-08-28 so the detailed basemap paints over
      // it — still below every vector/marker pane, so it stays ground, never
      // figure, and it remains the visible floor whenever the archive is
      // unreachable.
      m.createPane(MAP_GROUND_PANE).style.zIndex = 150;
      // Detailed basemap (2026-08-28): our own Protomaps/OSM vector archive,
      // the thing that gives back the streets, parks and city names CARTO's
      // watermark cost us. Resolves null — and changes nothing — if the
      // archive is unreachable. See lib/basemap-detail.js.
      if (typeof window.attachDetailBasemap === 'function') {
        window.attachDetailBasemap(m, { darkMode }).then((layer) => {
          // Theme flip / unmount can retire this map before the archive
          // answers; don't leak a layer onto a dead one.
          if (layer && mapRef.current !== m) {
            try { m.removeLayer(layer); } catch (_) { /* already gone */ }
          }
        });
      }
    }
    // Restyle Leaflet's own control to the theme: near-opaque scrim so the
    // credit clears WCAG AA regardless of the surface behind it. In 'self'
    // mode this carries only Leaflet's own prefix.
    const attrEl = m.attributionControl && m.attributionControl.getContainer();
    if (attrEl) {
      attrEl.style.cssText = darkMode
        ? 'background:rgba(12,15,15,0.92);color:#94897B;font:10px/1.7 var(--font-body,sans-serif);padding:0 6px;'
        : 'background:rgba(251,249,244,0.92);color:#6B5B4F;font:10px/1.7 var(--font-body,sans-serif);padding:0 6px;';
      attrEl.querySelectorAll('a').forEach((a) => {
        a.style.color = 'inherit';
        a.style.textDecoration = 'underline';
      });
    }
    mapRef.current = m;
    m.on('click', () => { setSelectedEvent(null); setCardPos(null); });
    return () => { m.remove(); mapRef.current = null; };
  }, [darkMode, leafletReady]);

  // ------ Self-basemap ground: land polygons over the water container ------
  // Re-runs when the map re-inits (darkMode/leafletReady recreate it above —
  // the init effect is declared first, so the new map exists by the time this
  // fires) and when the artifact arrives. Null baseGeo (fetch failed)
  // degrades to bare water ground + pins — page-never-dies.
  React.useEffect(() => {
    const m = mapRef.current;
    const L = window.L;
    if (!m || !L || !baseGeo) return;
    if (mapBasemapMode() !== 'self') return;
    const G = darkMode ? MAP_GROUND.dark : MAP_GROUND.light;
    const ground = L.geoJSON(baseGeo, {
      pane: MAP_GROUND_PANE,
      interactive: false,
      style: {
        fillColor: G.land, fillOpacity: 1,
        color: G.coast, weight: 1, opacity: 1, // hairline coastline
      },
    }).addTo(m);
    return () => { if (mapRef.current === m) m.removeLayer(ground); };
  }, [leafletReady, baseGeo, darkMode]);

  React.useEffect(() => {
    const m = mapRef.current;
    if (!m || !leafletReady || !window.L) return;
    if (layerRef.current) { m.removeLayer(layerRef.current); }
    searchMarkersRef.current = {}; // Goal B: rebuilt with the layer below
    const group = window.L.markerClusterGroup({
      maxClusterRadius: 45,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      animate: true,
    });
    const catColors = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
      'MUSIC': '#FF4F00', 'CARDS': '#7A99AC', 'ART': '#5A7A68',
      'FOOD': '#D57800', 'NIGHTLIFE': '#7C5BBC', 'COMMUNITY': '#395542',
    };
    events.forEach(ev => {
      if (ev.lat == null || ev.lng == null) return;
      // Pin colour is CATEGORY only. The `|| TAG_COLORS[ev.tag]` leg that sat
      // in the middle here fell back to a CITY hue for an uncategorized event
      // — a dot whose colour meant "where" sitting in a legend that means
      // "what". It also can't survive the city-registry move: cityInk returns
      // an rgba neutral for non-anchor cities, and the hex guard below would
      // reject it into this same default anyway.
      const rawColor = catColors[ev.category] || '#FF4F00';
      const color = /^#[0-9A-Fa-f]{3,6}$/.test(rawColor) ? rawColor : '#FF4F00';
      const icon = window.L.divIcon({
        className: 'onsumn-pin',
        html: `<span class="onsumn-pin-dot" style="background:${color}"></span><span class="onsumn-pin-ring" style="border-color:${color}"></span>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      // Accessible name for the pin. Leaflet stamps role="button" + tabindex=0
      // on keyboard-focusable markers but leaves them nameless (aria-command-name
      // failure). `alt` is Leaflet's documented a11y hook; we also set aria-label
      // directly on the divIcon element so the .onsumn-pin <div> (not an <img>)
      // exposes a non-empty name to screen readers.
      const pinName = ev.venue ? `${ev.title} at ${ev.venue}` : ev.title;
      const marker = window.L.marker([ev.lat, ev.lng], {
        icon,
        keyboard: true,
        alt: pinName,
        title: pinName,
      });
      marker.on('add', () => {
        const el = marker.getElement();
        if (el && pinName) el.setAttribute('aria-label', pinName);
      });
      marker.on('click', (e) => {
        window.L.DomEvent.stopPropagation(e);
        const pt = m.latLngToContainerPoint(e.latlng);
        const container = ref.current;
        setCardPos({
          x: pt.x, y: pt.y,
          containerW: container ? container.clientWidth : 600,
          containerH: container ? container.clientHeight : 480,
        });
        setSelectedEvent(ev);
      });
      // Goal B: register the marker so a search select can resolve its
      // cluster state (zoomToShowLayer) and anchor the card to the real pin.
      const searchKey = ev.slug || ev.id;
      if (searchKey != null) searchMarkersRef.current[searchKey] = marker;
      group.addLayer(marker);
    });
    group.addTo(m);
    layerRef.current = group;
    /* Fit map to show all event pins */
    const validEvents = events.filter(e => e.lat != null && e.lng != null);
    if (validEvents.length > 1) {
      const bounds = window.L.latLngBounds(validEvents.map(e => [e.lat, e.lng]));
      m.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [events, leafletReady]);

  /* ── Goal B: search select → fly → open the SHARED card (R3/R4) ──
     Routes INTO the existing marker-click state path (setCardPos +
     setSelectedEvent) — never a parallel opener. The open is DEFERRED to
     'moveend' because the card positions from latLngToContainerPoint, which
     is wrong mid-flyTo. R4 edges:
       (a) zero-delta — a flyTo to the current center/zoom may emit NO
           moveend, so an epsilon compare opens immediately instead of
           hanging on a listener that never fires;
       (b) cluster — the flown-to event can sit inside a cluster bubble at
           the landing zoom; markercluster's own zoomToShowLayer unclusters
           (or spiderfies) and the card opens in its callback, anchored to
           the real pin.
     flySeqRef supersedes stale callbacks when a second select lands
     mid-flight — the first card must never open at the second target. */
  const openSearchCard = React.useCallback((ev, seq) => {
    const reveal = () => {
      if (seq != null && flySeqRef.current !== seq) return; // superseded
      const m = mapRef.current;
      if (!m) return;
      const pt = m.latLngToContainerPoint([ev.lat, ev.lng]);
      const container = ref.current;
      setCardPos({
        x: pt.x, y: pt.y,
        containerW: container ? container.clientWidth : 600,
        containerH: container ? container.clientHeight : 480,
      });
      // Solicited navigation (Iris): every reveal() is a SEARCH-SELECT open
      // (both moveend-settle and zero-delta paths, incl. zoomToShowLayer's
      // callback) — flag it so the selectedEvent effect focuses the card's
      // Close button after this commit. Marker-path opens never come here.
      searchOpenFocusRef.current = true;
      setSelectedEvent(ev);
    };
    const group = layerRef.current;
    const searchKey = ev.slug || ev.id;
    const marker = searchKey != null ? searchMarkersRef.current[searchKey] : null;
    if (marker && group
      && typeof group.getVisibleParent === 'function'
      && typeof group.zoomToShowLayer === 'function') {
      const parent = group.getVisibleParent(marker);
      if (parent && parent !== marker) {
        group.zoomToShowLayer(marker, reveal);
        return;
      }
    }
    // No marker on the map for this event (filtered out, coordless map set,
    // or — since D3 — a row the client net collapsed while search still
    // resolves its twin's coordinates): the shared card still opens at the
    // flown-to point. This branch is PROVEN reachable, not asserted benign:
    // tests/map-search.test.js "no-marker select still opens the shared
    // card" drives a select for an event with no registered marker and
    // requires the card to open here.
    reveal();
  }, []);

  const flyThenOpen = React.useCallback((latlng, zoom, ev) => {
    const m = mapRef.current;
    if (!m) return;
    flySeqRef.current += 1;
    const seq = flySeqRef.current;
    if (pendingMoveendRef.current && typeof m.off === 'function') {
      m.off('moveend', pendingMoveendRef.current);
      pendingMoveendRef.current = null;
    }
    const settle = () => {
      pendingMoveendRef.current = null;
      if (flySeqRef.current !== seq) return; // a newer select superseded this fly
      if (ev) openSearchCard(ev, seq);
    };
    const c = typeof m.getCenter === 'function' ? m.getCenter() : null;
    const z = typeof m.getZoom === 'function' ? m.getZoom() : null;
    const alreadyThere = c && z != null
      && Math.abs(c.lat - latlng[0]) < FLY_EPSILON_DEG
      && Math.abs(c.lng - latlng[1]) < FLY_EPSILON_DEG
      && Math.abs(z - zoom) < 0.01;
    if (alreadyThere) {
      settle(); // zero-delta: moveend may never come — open now
      return;
    }
    pendingMoveendRef.current = settle;
    // Registered BEFORE the camera move — a NON-ANIMATED setView can fire
    // moveend synchronously (Iris footgun: registering after would miss it).
    m.once('moveend', settle);
    // Iris co-sign: reduced motion snaps instead of flying. Map-qualified
    // m.setView — NEVER bare setView (a React state setter by that name
    // lives in EventsMapSection below).
    if (reducedMotion && typeof m.setView === 'function') m.setView(latlng, zoom);
    else m.flyTo(latlng, zoom);
  }, [openSearchCard, reducedMotion]);

  /* Venue coords: the live search rail (lib/search-venue-rail.js) carries NO
     lat/lng and no client-side geocode source is loaded, so R3's "venue
     coords from the venue entry" is satisfied by DERIVING them from the
     venue's own pinned events — every spine event carries its venue's
     authority coordinates (data/venue-geocodes.json upstream). Scan the
     map's events first, then the full search index. No geocoder, no network
     (INOCULATION). Documented as an R3 deviation in the Goal B report. */
  const venueCoordsFor = React.useCallback((venue) => {
    if (!venue) return null;
    const names = [venue.canonical, venue.short]
      .concat(Array.isArray(venue.aliases) ? venue.aliases : [])
      .filter(Boolean)
      .map((s) => String(s).toLowerCase().trim());
    const canonical = String(venue.canonical || '').toLowerCase().trim();
    const matches = (e) => {
      if (!e || e.lat == null || e.lng == null) return false;
      const v = String(e.venue || '').toLowerCase().trim();
      if (!v) return false;
      return names.indexOf(v) !== -1
        || (!!canonical && (v.indexOf(canonical) !== -1 || canonical.indexOf(v) !== -1));
    };
    const own = (events || []).find(matches);
    if (own) return [own.lat, own.lng];
    const idx = (typeof window !== 'undefined' && window.__SEARCH_INDEX__) || null;
    if (idx && Array.isArray(idx.events)) {
      const hit = idx.events.find((n) => n && matches(n.raw));
      if (hit) return [hit.raw.lat, hit.raw.lng];
    }
    return null;
  }, [events]);

  const handleSearchSelect = React.useCallback((item) => {
    if (!item) return;
    if (item.kind === 'venue') {
      const coords = venueCoordsFor(item.venue);
      if (coords) flyThenOpen(coords, SEARCH_FLY_ZOOM, null); // a venue is a place — no event card
      return;
    }
    const ev = item.event;
    if (!ev || ev.lat == null || ev.lng == null) return; // coordless events can't be flown to
    flyThenOpen([ev.lat, ev.lng], SEARCH_FLY_ZOOM, ev);
  }, [venueCoordsFor, flyThenOpen]);

  /* ── Goal B: fenced locate (R5 — the FlowsApp.jsx:277-311 shape) ──
     getCurrentPosition fires ONLY here, on the control tap — never on
     mount. Coords feed ONE flyTo and die: never state / URL / share-state /
     localStorage / KV / console / analytics / any fetch (Atlas fence, gated
     across all four vectors in tests/map-search.test.js). Ephemeral, no
     account — no personalization until >200 weekly events. */
  const handleLocate = React.useCallback(() => {
    // Iris + Rhea live-region symmetry: the tap announces immediately…
    setLocateState({ kind: 'finding' });
    const denied = () => setLocateState({ kind: 'denied' });
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      // No API at all → the same visible denied path (FlowsApp precedent).
      denied();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const m = mapRef.current;
        if (m) {
          // Reduced motion snaps (m.setView — map-qualified) instead of flying.
          if (reducedMotion && typeof m.setView === 'function') m.setView([latitude, longitude], LOCATE_FLY_ZOOM);
          else m.flyTo([latitude, longitude], LOCATE_FLY_ZOOM);
        }
        // latitude/longitude go out of scope right here — nothing retains
        // them. …and success announces a STATIC string (Atlas fence).
        setLocateState({ kind: 'centered' });
      },
      () => denied(),
      { timeout: 8000, maximumAge: 300000 }
    );
  }, [reducedMotion]);

  return (
    <div style={{
      position: 'relative',
      ...glassStyle(darkMode, 'card'),
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
      borderRadius: radius,
      overflow: 'hidden',
      boxShadow: darkMode
        ? 'inset 0 0 30px rgba(0,0,0,0.4), 0 8px 32px rgba(0,0,0,0.3)'
        : 'inset 0 0 20px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08)',
    }}>
      {/* In 'self' mode the container IS the water — inline style outranks
          Leaflet's default grey. Both themes intentional
          (feedback_dark_light_mode). Carto mode keeps tiles as ground. */}
      <div ref={ref} style={{
        width: '100%', height,
        background: mapBasemapMode() === 'self'
          ? (darkMode ? MAP_GROUND.dark.water : MAP_GROUND.light.water)
          : 'var(--surface-dim)',
      }} />
      {/* Loading state while Leaflet code-splits in (Issue #44 Option B).
          Shown until window.L is initialized — typically <300ms on modern
          mobile networks (CDN-cached after first map view). Pulse uses
          --international-orange via livePulse keyframes already defined in
          index.html, so dark/light mode is automatic. */}
      {!leafletReady && !loadError && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute', inset: 0, zIndex: 400,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 12,
            background: darkMode ? 'rgba(10,10,10,0.6)' : 'rgba(239,239,239,0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: 'var(--international-orange)',
              boxShadow: '0 0 8px var(--international-orange)',
              animation: 'livePulse 1.4s ease-in-out infinite',
            }}
          />
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)',
          }}>Loading map</span>
        </div>
      )}
      {/* Load error — rare, only fires on offline / CDN outage / SRI fail.
          Tells the user what happened without yelling. */}
      {loadError && (
        <div
          role="alert"
          style={{
            position: 'absolute', inset: 0, zIndex: 400,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '24px 32px', textAlign: 'center',
            background: darkMode ? 'rgba(10,10,10,0.9)' : 'rgba(239,239,239,0.9)',
          }}
        >
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
            color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
          }}>Map didn't load.</div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 12,
            color: darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
          }}>Check connection and reload. Pin list still works in Grid view.</div>
        </div>
      )}
      {/* Event count badge */}
      {!selectedEvent && leafletReady && <div style={{
        position: 'absolute', bottom: 12, left: 12, zIndex: 500,
        ...glassStyle(darkMode, 'btn'),
        border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.3)',
        borderRadius: Math.max(radius - 4, 8),
        padding: '8px 14px',
        fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
      }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
          background: 'var(--international-orange)',
          boxShadow: '0 0 6px var(--international-orange)',
          animation: 'livePulse 2s ease-in-out infinite',
          marginRight: 6, verticalAlign: 'middle',
        }} />{events.length} EVENTS · THE BAY, RIGHT NOW
      </div>}
      {/* ── Goal B: search + locate — the map becomes an instrument, not a
          display. Overlay at chrome tier 1000 (clears Leaflet's panes at
          600/700 and beats its control corner's 1000 by DOM order; the card
          at 1001 stays above everything). SearchBar is the SHARED Spotlight
          component in opt-in map mode (onResultSelect) — the Header's
          page-level instance stays prop-less and keeps owning ⌘K. */}
      {leafletReady && (
        <div style={{
          position: 'absolute', top: 10, left: 10, right: 10, zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
          gap: 8, pointerEvents: 'none',
        }}>
          {typeof window !== 'undefined' && typeof window.SearchBar === 'function' && (
            <div style={{
              pointerEvents: 'auto', position: 'relative', minWidth: 0,
              flex: (typeof window !== 'undefined' && window.innerWidth <= 768) ? '1 1 auto' : '0 1 auto',
              maxWidth: 'min(440px, 100%)',
            }}>
              <window.SearchBar
                key={searchEpoch}
                darkMode={darkMode}
                isMobile={typeof window !== 'undefined' && window.innerWidth <= 768}
                onResultSelect={handleSearchSelect}
              />
            </div>
          )}
          <button
            type="button"
            onClick={handleLocate}
            aria-label="Center map on your location"
            className="focus-double-ring"
            style={{
              pointerEvents: 'auto', flex: '0 0 auto',
              width: 44, height: 44, minWidth: 44, minHeight: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...glassStyle(darkMode, 'btn'),
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.18)',
              borderRadius: Math.max(radius - 4, 8),
              color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.72)',
              cursor: 'pointer',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="7" />
              <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
          </button>
        </div>
      )}
      {/* Locate status — ONE aria-live container stays MOUNTED (R5 + Iris
          symmetry): tap → "Finding your location…" and success → "Centered
          on your location." are visually-hidden announcements; denial keeps
          the VISIBLE chip (Rhea-approved copy, unchanged). Static strings
          only — never coordinates (Atlas fence). */}
      <div aria-live="polite" style={{
        position: 'absolute', top: 62, right: 10, zIndex: 1000,
        maxWidth: 280, pointerEvents: 'none',
      }}>
        {locateState && locateState.kind === 'denied' && (
          <div data-locate-status="denied" style={{
            ...glassStyle(darkMode, 'btn'),
            border: darkMode ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.14)',
            borderRadius: Math.max(radius - 6, 8),
            padding: '8px 12px',
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
            lineHeight: 1.4,
            color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)',
          }}>{LOCATE_DENIED_COPY}</div>
        )}
        {locateState && locateState.kind !== 'denied' && (
          <span style={{
            position: 'absolute', left: -10000, top: 'auto',
            width: 1, height: 1, overflow: 'hidden',
          }}>{locateState.kind === 'finding' ? LOCATE_FINDING_COPY : LOCATE_CENTERED_COPY}</span>
        )}
      </div>
      {/* Selected event card */}
      <MapEventCard
        event={selectedEvent}
        darkMode={darkMode}
        radius={radius}
        position={cardPos}
        onCategoryClick={onCategoryClick}
        feedGenerated={feedGenerated}
        onClose={() => { setSelectedEvent(null); setCardPos(null); }}
      />
    </div>
  );
}

/* ───── Time-of-day filter helper ───── */
function matchesTimeFilter(event, times) {
  if (!times || times.length === 0) return true;
  const t = event.time || '';
  const match = t.match(/(\d+)\s*(AM|PM)/i);
  if (!match) return true;
  let hour = parseInt(match[1]);
  const period = match[2].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  return times.some(slot => {
    if (slot === 'Morning') return hour >= 6 && hour < 12;
    if (slot === 'Afternoon') return hour >= 12 && hour < 17;
    if (slot === 'Evening') return hour >= 17 && hour < 21;
    if (slot === 'Late Night') return hour >= 21 || hour < 6;
    return true;
  });
}

/* ───── Date filter helper ───── */
function matchesDateFilter(event, filter) {
  if (filter === 'ALL') return true;
  const now = new Date();
  const eventDateStr = event.date || '';
  // Parse "Mon DD" format (e.g., "Apr 25")
  const match = eventDateStr.match(/^(\w+)\s+(\d+)$/);
  if (!match) return true; // Can't parse, show it
  const months = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
  const monthIdx = months[match[1]];
  if (monthIdx === undefined) return true;
  const day = parseInt(match[2]);
  const eventDate = new Date(now.getFullYear(), monthIdx, day);
  // If event month is far behind current month, it's probably next year
  if (monthIdx < now.getMonth() - 1) eventDate.setFullYear(now.getFullYear() + 1);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

  if (filter === 'TODAY') return eventDay.getTime() === today.getTime();
  if (filter === 'TOMORROW') return eventDay.getTime() === tomorrow.getTime();
  if (filter === 'WEEKEND') {
    const sat = new Date(today);
    sat.setDate(today.getDate() + (6 - today.getDay()));
    const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
    return eventDay.getTime() === sat.getTime() || eventDay.getTime() === sun.getTime();
  }
  return true;
}

/* ───── Chronological sort key ─────
   Defines "soonest first" for the homepage carousel rows (rendered by
   EventsMapSection below). Prefers `expiresAt` (ISO, written by the
   worker as end-of-day +1 of the show). Falls back to inferring a year for
   the bare "Mon DD" `date` — if past, roll to next year. Returns ms since
   epoch; +Infinity for undatable events so they sink to the bottom.
   Inlined here (vs imported from a lib) because index.html script-tag wiring
   has been touched by a parallel branch — keeping the change Map.jsx-local
   avoids merge collisions with perf/chunked-event-render. */
/* ── Freshness rule (Q4, 2026-07-28) — the ONE place "daily" is decided ──
   A freshness claim is earned, not asserted (pique G2 provenance): signal =
   the events.json build stamp; true iff it exists, parses, and is under 36h
   old. Fail-closed: null/garbage/wrong type → false — unknown age is the
   missing case, not a pass. 36h not 24h: the claim survives one cron hiccup
   window, dies before day two. Epoch-ms comparison only — no calendar math
   (the "3 AM" bug class stays dead). Consumers: the events-header subtitle
   below and Hero's sub via index-main's updatedRecently. Gated:
   tests/hero-freshness.test.js. */
function isFreshDaily(generatedIso, nowMs) {
  if (typeof generatedIso !== 'string' || !generatedIso) return false;
  const t = Date.parse(generatedIso);
  if (Number.isNaN(t)) return false;
  return ((typeof nowMs === 'number' ? nowMs : Date.now()) - t) < 36 * 3600 * 1000;
}

const _MAP_MONTH_IDX = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
function mapEventSortKey(ev, nowMs) {
  if (ev && typeof ev.expiresAt === 'string') {
    const t = Date.parse(ev.expiresAt);
    if (!Number.isNaN(t)) return t;
  }
  const raw = ev && typeof ev.date === 'string' ? ev.date.trim() : '';
  if (!raw) return Infinity;
  if (/^today$/i.test(raw)) return nowMs;
  const cleaned = raw.replace(/^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[a-z]*,?\s*/i, '').trim();
  const m = cleaned.match(/^([A-Za-z]+)\s+(\d{1,2})$/);
  if (!m) return Infinity;
  const monthIdx = _MAP_MONTH_IDX.indexOf(m[1].slice(0, 3).toLowerCase());
  if (monthIdx < 0) return Infinity;
  const day = parseInt(m[2], 10);
  if (!day || day > 31) return Infinity;
  const now = new Date(nowMs);
  let year = now.getUTCFullYear();
  const candidate = Date.UTC(year, monthIdx, day);
  const todayMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (candidate < todayMidnight) year += 1;
  return Date.UTC(year, monthIdx, day);
}

/* ───── Events + Map Section (merges glass grid + Leaflet map) ───── */
function EventsMapSection({ darkMode, activeCity, radius, taste, onOpenTastePicker }) {
  // `generated` = the events.json build stamp (_eventsGenerated in Events.jsx)
  // — threaded to MapEventCard so its registry-fed tonight pill measures the
  // same freshness ceiling as the grid's date-proximity badge.
  const { events: allEvents, loading, generated } = useEvents();
  // Mobile defaults to GRID (Split shows a 620px map above carousel rows and
  // eats the entire first screen on phones). User choice persists in localStorage.
  // Calendar is the 4th view (Mav-overridden density model — see CalendarView.jsx).
  const [view, setView] = React.useState(() => {
    // ?view=calendar is the calendar's address (header link, /calendar
    // redirect). It wins for THIS load only and is not written back as the
    // remembered preference — only a toggle is.
    try {
      if (new URLSearchParams(window.location.search).get('view') === 'calendar') return 'calendar';
    } catch {}
    try {
      const saved = localStorage.getItem('onsumn-view-mode');
      if (saved === 'map' || saved === 'split' || saved === 'grid' || saved === 'calendar') return saved;
    } catch {}
    return (typeof window !== 'undefined' && window.innerWidth <= 768) ? 'grid' : 'split';
  });
  const [switching, setSwitching] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [dateFilter, setDateFilter] = React.useState('ALL');
  // City facet cut in FilterPopup distill pass (2026-06-02) — Map view +
  // Header city nav already cover location, so the popup's third filter was
  // redundant. Shape preserved minus `cities`; default arrays keep any legacy
  // consumer from blowing up if it lands here mid-deploy.
  const [activeFilters, setActiveFilters] = React.useState({ categories: [], times: [], sounds: [], dates: [] });
  const [page, setPage] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const carouselRef = React.useRef(null);
  const EVENTS_PER_PAGE = 12;

  // Self-basemap geography (Natural Earth land, committed — the same
  // artifact /flows fetches). Degrades gracefully: a failed fetch leaves the
  // pulse map on its bare water ground.
  const [baseGeo, setBaseGeo] = React.useState(null);
  React.useEffect(() => {
    fetch('data/bay-context.geojson').then(r => (r.ok ? r.json() : null))
      .then(setBaseGeo).catch(() => setBaseGeo(null));
  }, []);

  /* Draggable section order — persisted to localStorage */
  const DEFAULT_ORDER = ['MUSIC', 'ART', 'FOOD', 'NIGHTLIFE', 'COMMUNITY', 'CARDS'];
  const [sectionOrder, setSectionOrder] = React.useState(() => {
    try {
      const saved = localStorage.getItem('onsumn-section-order');
      if (saved) {
        const parsed = JSON.parse(saved);
        const missing = DEFAULT_ORDER.filter(c => !parsed.includes(c));
        return [...parsed.filter(c => DEFAULT_ORDER.includes(c)), ...missing];
      }
    } catch {}
    return DEFAULT_ORDER;
  });
  const [liftedCat, setLiftedCat] = React.useState(null);
  const rowRefs = React.useRef({});
  const dragState = React.useRef(null);
  const scrollAnimRef = React.useRef(null);

  /* Batch-fetch interest counts for visible events */
  React.useEffect(() => {
    if (!allEvents.length) return;
    const slugs = allEvents.filter(e => e.slug).map(e => e.slug).slice(0, 50);
    fetchInterestCounts(slugs);
  }, [allEvents]);

  /* The header lights its CALENDAR pill from this event (index-main.jsx
   * listens). Fires on mount too, so a remembered calendar view lights it. */
  React.useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('onsumn:view', { detail: view })); } catch {}
  }, [view]);

  /* Deep-link: ?view=calendar (the /calendar address) brings the section up
   * once the calendar has mounted — the same courtesy #event-<slug> pays a
   * card. Offset clears the fixed header. */
  React.useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('view') !== 'calendar') return;
    } catch { return; }
    const t = setTimeout(() => {
      const el = document.getElementById('events');
      if (!el || typeof window.scrollTo !== 'function') return;
      const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88, behavior: reduce ? 'auto' : 'smooth' });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  /* Deep-link: ?cat=<Label> applies a category filter on mount. Used by the
   * footer category links (Sections.jsx) so a click on "Music" lands on the
   * events grid with the Music filter applied. Label is mixed-case to match
   * FilterPopup's selected-state check (see openFilterForCategory comment). */
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('cat');
      if (cat) {
        setActiveFilters(prev => ({ ...prev, categories: [cat] }));
      }
    } catch {}
  }, []);

  /* Pointer-based drag: smooth, no HTML5 DnD ghost.
   * Works for mouse, pen, and touch. Touch gets a small drag-threshold so
   * a tap on the handle doesn't accidentally lift the row, and the handle
   * element itself uses `touchAction: 'none'` so starting a gesture on it
   * doesn't scroll the page. setPointerCapture keeps the row tracking even
   * when the finger drifts off the small handle target. */
  const handlePointerDown = React.useCallback((e, cat) => {
    e.preventDefault();
    const rowEl = rowRefs.current[cat];
    if (!rowEl) return;

    const isTouch = e.pointerType === 'touch';
    /* Touch needs to move past a small threshold before we commit to the
     * drag — protects against scroll-init taps that briefly land on the
     * handle. Mouse/pen lifts immediately. */
    const DRAG_THRESHOLD = isTouch ? 6 : 0;
    let lifted = !isTouch;

    /* Hold the pointer on the handle element so subsequent move/up events
     * route here even if the finger drifts onto a sibling row.
     * (On iOS Chromium browsers we've seen setPointerCapture interfere with
     * window.scrollBy during the captured gesture — if scroll feels stuck
     * again, this is the first thing to try removing.) */
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}

    const rect = rowEl.getBoundingClientRect();
    const startY = e.clientY;
    const startScroll = window.scrollY;

    const liftRow = () => {
      if (lifted) return;
      lifted = true;
      setLiftedCat(cat);
      rowEl.style.transition = 'transform 0.08s ease-out, box-shadow 0.2s ease';
      rowEl.style.zIndex = '50';
      rowEl.style.transform = 'scale(1.015)';
      rowEl.style.boxShadow = '0 12px 40px rgba(0,0,0,0.35)';
      rowEl.style.borderRadius = '16px';
      rowEl.style.pointerEvents = 'none';
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    };

    /* Lift immediately for mouse/pen; defer for touch until past threshold. */
    if (!isTouch) liftRow();

    let currentY = startY;

    /* Recompute the row's translateY + hit-test swap. Pulled into its own
     * function because it has to run on BOTH pointermove (finger moved) AND
     * inside scrollLoop (page auto-scrolled while finger held still). If we
     * only updated on pointermove, holding the finger at the top edge would
     * scroll the page but leave the row's transform stale — the row would
     * visually drift 14px down every frame as the page scrolled 14px up,
     * stranding the user and forcing the "section by section" workaround
     * Marco hit. */
    const tickRowPosition = () => {
      const scrollDelta = window.scrollY - startScroll;
      const dy = currentY - startY + scrollDelta;

      if (!lifted) {
        if (Math.abs(dy) < DRAG_THRESHOLD) return;
        liftRow();
      }

      rowEl.style.transform = `translateY(${dy}px) scale(1.015)`;

      /* Hit-test other rows for swap */
      const liftedMid = rect.top + rect.height / 2 + dy;
      setSectionOrder(prev => {
        const curIdx = prev.indexOf(cat);
        let swapIdx = -1;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i] === cat) continue;
          const otherEl = rowRefs.current[prev[i]];
          if (!otherEl) continue;
          const otherRect = otherEl.getBoundingClientRect();
          const otherMid = otherRect.top + otherRect.height / 2;
          if (curIdx < i && liftedMid > otherMid) swapIdx = i;
          if (curIdx > i && liftedMid < otherMid) swapIdx = i;
        }
        if (swapIdx !== -1 && swapIdx !== curIdx) {
          const next = [...prev];
          next.splice(curIdx, 1);
          next.splice(swapIdx, 0, cat);
          localStorage.setItem('onsumn-section-order', JSON.stringify(next));
          return next;
        }
        return prev;
      });
    };

    /* Auto-scroll near edges (only when actually dragging).
     * Edge zone 140px, speed 14px/frame (~840px/sec at 60fps) — fast enough
     * to traverse a long page without feeling stuck, slow enough that the
     * row position stays legible. Lower bound `currentY > 0` removed — on
     * touch the finger can land at clientY=0 (literal top edge), which the
     * old check excluded and left the user stranded. */
    const scrollLoop = () => {
      if (lifted) {
        const edgeZone = 140;
        const speed = 14;
        let didScroll = false;
        if (currentY < edgeZone) {
          const intensity = 1 - (Math.max(0, currentY) / edgeZone);
          window.scrollBy(0, -speed * intensity);
          didScroll = true;
        } else if (currentY > window.innerHeight - edgeZone) {
          const intensity = 1 - ((window.innerHeight - currentY) / edgeZone);
          window.scrollBy(0, speed * intensity);
          didScroll = true;
        }
        /* Auto-scroll moved the page — re-tick the row position so it
         * keeps up with the finger even when the finger itself is still. */
        if (didScroll) tickRowPosition();
      }
      scrollAnimRef.current = requestAnimationFrame(scrollLoop);
    };
    scrollAnimRef.current = requestAnimationFrame(scrollLoop);

    /* iOS Chromium fix: RAF can lag behind the actual scroll during a touch
     * gesture, so tickRowPosition called from inside scrollLoop sometimes
     * reads a stale window.scrollY → dy lags by 1-2 frames → row drifts
     * down/up away from the finger. Subscribing to the real `scroll` event
     * gives us a ground-truth signal: whenever the page actually moved, we
     * re-tick the row immediately with the fresh scrollY. Passive listener
     * so we never block scroll. */
    const onScroll = () => {
      if (lifted) tickRowPosition();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const onPointerMove = (ev) => {
      currentY = ev.clientY;
      tickRowPosition();
    };

    const onPointerUp = () => {
      /* Settle the row back into place */
      rowEl.style.transition = 'transform 0.35s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.35s ease, border-radius 0.35s ease';
      rowEl.style.transform = '';
      rowEl.style.boxShadow = '';
      rowEl.style.borderRadius = '';
      rowEl.style.zIndex = '';
      rowEl.style.pointerEvents = '';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setLiftedCat(null);

      if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('scroll', onScroll);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, []);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleViewChange = (v) => {
    if (v === view) return;
    setSwitching(true);
    setTimeout(() => {
      setView(v);
      try { localStorage.setItem('onsumn-view-mode', v); } catch {}
      // Keep the address honest: copy-link from the calendar shares the
      // calendar; leaving it drops the param. replaceState, so view
      // switches never pile up in the back button.
      try {
        const url = new URL(window.location.href);
        if (v === 'calendar') url.searchParams.set('view', 'calendar');
        else url.searchParams.delete('view');
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
      } catch {}
      setSwitching(false);
    }, 250);
  };

  const totalFilters = activeFilters.categories.length + activeFilters.times.length + activeFilters.sounds.length + (activeFilters.dates?.length || 0);

  /* Header city nav still works — primary location filter lives here now.
     Hoisted out of the predicate so the key is normalized ONCE, not 1675
     times, and so the two sides are visibly normalized by the same function.
     A row with no established city has an empty key: it matches ALL (it is a
     real event and belongs in the feed) and matches no named city (we cannot
     assert it is in one). That asymmetry is the whole rule in two lines. */
  const activeCityKey = normalizeCityKey(activeCity);
  const cityFilterOff = !activeCityKey || activeCityKey === 'ALL';

  const filtered = allEvents.filter(e => {
    const headerCityMatch = cityFilterOff || normalizeCityKey(e.city) === activeCityKey;
    /* Popup category filter */
    const catMatch = activeFilters.categories.length === 0
      || activeFilters.categories.some(c => c.toUpperCase() === e.category);
    /* Popup time filter */
    const timeMatch = matchesTimeFilter(e, activeFilters.times);
    /* Sub-genre filter — strict match. Pre-fix this had a lenient
       `|| !e.subgenres || e.subgenres.length === 0` fallback that let
       any untagged event through when sounds were selected, which made
       the Sounds filter inert (no event had subgenres). Now:
       1. Prefer baked event.subgenres if the scraper ran the classifier.
       2. Fall back to window.classifySubgenre at runtime for legacy rows.
       3. If still empty, the event is filtered OUT when sounds are picked
          — "select EDM" means EDM-only per Marco. */
    let eventSubgenres = (Array.isArray(e.subgenres) && e.subgenres.length > 0)
      ? e.subgenres
      : (typeof window !== 'undefined' && typeof window.classifySubgenre === 'function'
          ? window.classifySubgenre(e)
          : []);
    const soundMatch = activeFilters.sounds.length === 0
      || activeFilters.sounds.some(s => eventSubgenres.includes(s));

    /* Date quick-pick filter */
    const dateMatch = matchesDateFilter(e, dateFilter);

    /* Popup date filter (from filter popup "When" section) */
    const popupDateMatch = !activeFilters.dates || activeFilters.dates.length === 0
      || activeFilters.dates.some(d => {
        const key = d.toUpperCase().replace('THIS ', '');
        return matchesDateFilter(e, key);
      });

    return headerCityMatch && catMatch && timeMatch && soundMatch && dateMatch && popupDateMatch;
  });

  /* Sort: the main list is CHRONOLOGICAL, soonest-first — full stop. Taste
     NEVER reorders it. (CLAUDE.md sort doctrine: chronological is the
     default, hearts-boost the only sanctioned exception, personalization
     is additive rails — never a re-rank.) A previous revision re-sorted
     this entire array by tasteBoost, which silently put a matched show
     weeks out above an unmatched show happening that same night — killed
     2026-07-28, locked by tests/map-taste-order.test.js. Taste acts in one
     place only: the YOUR WAVE rail below (forYouEvents), which SELECTS
     matches from this list without reordering it. */
  const _nowMs = Date.now();
  const chronological = filtered
    .map((ev, i) => ({ ev, i, k: mapEventSortKey(ev, _nowMs) }))
    .sort((a, b) => (a.k - b.k) || (a.i - b.i))
    .map(x => x.ev);

  const totalPages = Math.ceil(chronological.length / EVENTS_PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages - 1, 0));
  const visible = chronological.slice(currentPage * EVENTS_PER_PAGE, (currentPage + 1) * EVENTS_PER_PAGE);

  /* Reset page when filters change */
  React.useEffect(() => { setPage(0); }, [activeCity, activeFilters, dateFilter]);

  /* ── Von Restorff hero (grid view) — ONE hero across the WHOLE feed ──
     The live homepage feed is these carousel rows, so the isolation
     effect is feed-scoped, not row-scoped: exactly one card in the entire
     grid — the first card of the first rendered row (top-left, prime real
     estate) — takes the hero treatment, and every other card in every row
     drops to the quiet field. One-hero-per-row would mint ~7 heroes and
     defeat the rarity that makes the effect work.
     Eligibility reuses isHeroCard (Events.jsx, window-attached): a hero
     exists only when the filtered feed holds >= HERO_MIN_FIELD (6) cards;
     below that every card renders the legacy (undefined-emphasis) glass.
     Filters re-run this on every render, so city/category/date narrowing
     re-crowns the new feed's top-left card — always exactly one, or none.
     Grid view only: split view's rows and the map cards stay legacy per
     scope (the map IS the hero of split view). */
  const forYouEvents = (taste && !taste.skipped)
    /* 15 SOONEST matches — date order inherited from `chronological`.
       (Pre-G1 this sliced a boost-ordered array = 15 highest-scoring.) */
    ? chronological.filter(e => tasteBoost(e, taste) > 0).slice(0, 15)
    : [];
  const heroRowTitle = (view === 'grid' && !loading
      && typeof isHeroCard === 'function' && chronological.length > 0
      && isHeroCard(chronological[0], 0, chronological))
    ? (forYouEvents.length > 0
        ? 'YOUR WAVE'
        : (sectionOrder.find(cat => chronological.some(e => e.category === cat)) || null))
    : null;
  const rowEmphasisRole = (title) =>
    heroRowTitle ? (title === heroRowTitle ? 'lead' : 'field') : undefined;

  /* ── OCCASION RAIL ── one rail per LIVE occasion that holds rows, AT MOST
     TWO (R11: three stacked rails pushed YOUR WAVE ~1,640px down a phone;
     the rest are one tap away on their hubs). R26 picks the two with the
     MOST tagged rows (uncapped); a tie goes to the sooner upcoming anchor,
     or between two passed anchors to the later one. The pair then shows
     upcoming anchors soonest first, then passed ones most recent first —
     so New Year's leads its own week instead of trailing Christmas.
     The anchor is the LIVE window's: this year's window, else last year's
     (a wrapping window holding a January today, windowContains's year
     order); a null anchor ranks last.
     Rows are a FILTER of `chronological` (sort doctrine: never a re-sort),
     painted to 12; `total` is the uncapped count the header prints (R14).
     Both orderings below are between OCCASIONS — never between events.
     heroRowTitle is untouched, so rowEmphasisRole(railTitle) is 'field'
     (every card quiet), or undefined on a sparse feed. Off season this is
     [] and the grid gets no rail and no empty state.
     tests/seasonal-rail.test.js reads every block between these anchor
     comments — keep both. */
  const occasionToday = pacificTodayIso();
  const occasionYear = Number(occasionToday.slice(0, 4));
  const liveAnchor = (entry) => {
    for (const y of [occasionYear, occasionYear - 1]) {
      const w = occasionWindow(entry, y);
      if (w && w.start <= occasionToday && occasionToday <= w.end) return occasionAnchor(entry, y);
    }
    return null;
  };
  // rank 0 = upcoming (anchor on/after today), 1 = passed, 2 = no anchor.
  const byAnchor = (a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.anchor === b.anchor) return a.rosterIdx - b.rosterIdx;
    const sooner = a.anchor < b.anchor ? -1 : 1;
    return a.rank === 0 ? sooner : -sooner;
  };
  const occasionRails = activeOccasions(occasionToday)
    .map((entry, rosterIdx) => {
      const rows = chronological.filter(e => e.occasion === entry.slug);
      const anchor = liveAnchor(entry);
      const rank = anchor === null ? 2 : anchor >= occasionToday ? 0 : 1;
      return { entry, rosterIdx, anchor, rank, events: rows.slice(0, 12), total: rows.length };
    })
    .filter(r => r.total > 0)
    .sort((a, b) => (b.total - a.total) || byAnchor(a, b)) // selection: most rows
    .slice(0, 2)
    .sort(byAnchor); // display: upcoming soonest first, then passed most recent first
  /* ── /OCCASION RAIL ── */

  /* Carousel scroll navigation */
  const scrollCarousel = (dir) => {
    const c = carouselRef.current;
    if (!c) return;
    const amount = c.clientWidth * 0.85;
    c.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  /* Chip → FilterPopup opener. Used by both MapView (pulse-map popup chip)
     and every CategoryCarouselRow card chip below. Converts UPPERCASE
     event.category to the mixed-case label FilterPopup uses for its
     selected-state check ("Music" not "MUSIC"). */
  const openFilterForCategory = React.useCallback((cat) => {
    if (!cat || typeof cat !== 'string') return;
    const label = cat.charAt(0) + cat.slice(1).toLowerCase();
    setActiveFilters(prev => ({ ...prev, categories: [label] }));
    setFilterOpen(true);
  }, []);

  return (
    <section id="events" style={{
      padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 32px)',
      maxWidth: 1280, margin: '0 auto',
      // scrollMarginTop accounts for the fixed Header height — Header.jsx's
      // city-pill click scrollIntoView's this section, and we don't want
      // it to sit behind the Header chrome.
      scrollMarginTop: 100,
    }}>
      {/* Filter Popup */}
      <FilterPopup
        darkMode={darkMode}
        radius={radius}
        activeFilters={activeFilters}
        onApply={(filters) => { setActiveFilters(filters); setPage(0); }}
        onClose={() => setFilterOpen(false)}
        visible={filterOpen}
      />

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        marginBottom: 40, flexWrap: 'wrap', gap: 16,
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700,
            color: darkMode ? 'var(--on-surface)' : '#1a1a1a', lineHeight: 1,
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: '-0.01em',
          }}>WHAT'S GOOD THIS WEEKEND</h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 14,
            color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)',
          }}>{isFreshDaily(generated) ? 'Fresh every day. The whole Bay.' : 'The whole Bay.'}</p>
        </div>

      </div>

      {/* Glass view toggle */}
      <div className="view-toggle" style={{
        display: 'inline-flex', gap: 2, marginBottom: 24,
        ...glassStyle(darkMode, 'panel'),
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        borderRadius: radius, padding: 3,
      }}>
        {[['map', 'Map'], ['split', 'Split'], ['grid', 'Grid'], ['calendar', 'Calendar']].map(([v, label]) => (
          <button key={v} aria-label={`${label} view`} aria-pressed={view === v} onClick={() => handleViewChange(v)} style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '8px 16px', minHeight: 44, borderRadius: Math.max(radius - 4, 0),
            background: view === v ? (darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)') : 'transparent',
            color: view === v ? (darkMode ? '#fff' : '#0A0A0A') : (darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)'),
            border: 'none', cursor: 'pointer',
            transition: 'all 0.25s',
          }}>{label}</button>
        ))}
      </div>

      {/* TUNE / RETUNE YOUR WAVE — permanent taste re-entry, ALL states.
          G2a (2026-07-28): the old `(!taste || taste.skipped)` gate made
          completing the picker a one-way door — the pill vanished and no
          surface anywhere could view, edit, or clear the picks. A
          preference you cannot revisit is one you cannot revoke, so the
          pill now renders whenever the picker is openable; only the label
          switches (hasTaste → RETUNE, draft flagged for Rhea). The
          data-taste-reentry hook is the overlay's focus-return fallback.
          window.TuneWavePill lives in TasteProfile.jsx, loaded before
          this file per index.html script order. */}
      {typeof onOpenTastePicker === 'function'
        && typeof window.TuneWavePill === 'function' && (() => {
          const TuneWavePill = window.TuneWavePill;
          return (
            <div data-taste-reentry style={{ display: 'inline-flex', marginLeft: 10, marginBottom: 24, verticalAlign: 'top' }}>
              <TuneWavePill darkMode={darkMode} hasTaste={!!(taste && !taste.skipped)} onClick={onOpenTastePicker} />
            </div>
          );
        })()}

      {/* Temporal quick-pick pills — on mobile we drop TOMORROW + WEEKEND
          (still reachable via MORE) so the row clears one line and the
          calendar grid grabs back ~50px of fold. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        gap: isMobile ? 4 : 6,
        marginBottom: isMobile ? 10 : 20,
        alignItems: 'center',
      }}>
        {['TODAY', 'TOMORROW', 'WEEKEND', 'ALL']
          .filter(label => !isMobile || label === 'TODAY' || label === 'ALL')
          .map(label => (
          <button key={label} aria-label={`Filter by ${label === 'ALL' ? 'all dates' : label.toLowerCase()}`} aria-pressed={dateFilter === label} onClick={() => setDateFilter(label)} style={{
            padding: isMobile ? '10px 14px' : '12px 18px',
            borderRadius: 100, minHeight: 44,
            fontFamily: 'var(--font-body)',
            fontSize: isMobile ? 11 : 12, fontWeight: 700,
            letterSpacing: '0.04em',
            cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
            border: dateFilter === label
              ? '2px solid var(--international-orange)'
              : (darkMode ? '2px solid rgba(255,255,255,0.12)' : '2px solid rgba(0,0,0,0.08)'),
            background: dateFilter === label
              ? 'var(--international-orange)'
              : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
            color: dateFilter === label
              ? '#10110F'
              : (darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.6)'),
            transform: dateFilter === label ? 'scale(1.05)' : 'scale(1)',
          }}>{label === 'ALL' ? 'ALL DATES' : label}</button>
        ))}

        {/* MORE filter button */}
        <button onClick={() => setFilterOpen(true)} style={{
          padding: isMobile ? '10px 14px' : '12px 16px',
          borderRadius: 100, minHeight: 44,
          fontFamily: 'var(--font-body)',
          fontSize: isMobile ? 11 : 12, fontWeight: 700,
          letterSpacing: '0.04em',
          cursor: 'pointer', transition: 'all 0.25s',
          border: totalFilters > 0
            ? '2px solid var(--international-orange)'
            : (darkMode ? '2px solid rgba(255,255,255,0.12)' : '2px solid rgba(0,0,0,0.08)'),
          background: 'transparent',
          color: totalFilters > 0
            ? 'var(--international-orange-ink)'
            : (darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.6)'),
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6h16M6 12h12M9 18h6"/>
          </svg>
          {totalFilters > 0 ? `MORE (${totalFilters})` : 'MORE'}
        </button>
      </div>

      <div className={`view-content ${switching ? 'switching' : ''}`}>
        {/* Map — hidden in Grid + Calendar views (Calendar replaces the
            carousels entirely; showing the Leaflet pulse map above it would
            double the page height and re-introduce the LCP cost we paid down
            in Issue #44). */}
        {view !== 'grid' && view !== 'calendar' && (
          <div style={{ marginBottom: view === 'split' ? 32 : 0 }}>
            <MapView
              events={filtered}
              darkMode={darkMode}
              height={view === 'map' ? 620 : 440}
              radius={radius}
              onCategoryClick={openFilterForCategory}
              feedGenerated={generated}
              baseGeo={baseGeo}
            />
          </div>
        )}

        {/* Calendar — 4th view. Takes over the carousel slot. Uses the same
            `filtered` event list so date / city / category / sounds chips
            up top still narrow the calendar. */}
        {view === 'calendar' && typeof window.CalendarView === 'function' && (
          <window.CalendarView
            events={filtered}
            darkMode={darkMode}
            radius={radius}
          />
        )}

        {/* Loading skeleton */}
        {loading && view !== 'map' && view !== 'calendar' ? (
          <div className="events-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16,
          }}>
            {[1,2,3,4].map(i => <SkeletonCard key={i} darkMode={darkMode} radius={radius} />)}
          </div>
        ) : view === 'map' || view === 'calendar' ? null : chronological.length === 0 ? (
          /* EmptyState names the city in its copy. It receives the CANONICAL
             city ("Berkeley", "Mountain View"), never a tag — the empty state
             is prose, and prose gets the claim, not the chip. `cityFilterOff`
             collapses both 'ALL' and an unresolvable value to no city at all,
             so the copy falls back to "the Bay" instead of naming something
             we could not resolve. */
          <EmptyState darkMode={darkMode} radius={radius} city={cityFilterOff ? 'ALL' : activeCity} category={activeFilters.categories.length === 1 ? activeFilters.categories[0] : null} />
        ) : (
          /* ── Category-Sectioned Carousels ── */
          <div>
            {/* ── OCCASION RAIL ── ABOVE YOUR WAVE, pinned by doctrine: no
                rowRef, no drag handle, never in the reorder set. The
                `occasion` prop selects the occasion header (EventsGrid.jsx,
                Zora §1). Amber accent = the YOUR WAVE precedent:
                CATEGORY_META has no slot for a free rail title, so the
                row's accentColor prop carries it. */}
            {occasionRails.map(({ entry, events, total }) => (
              <CategoryCarouselRow
                key={`occasion-${entry.slug}`}
                title={entry.railTitle}
                events={events}
                occasion={{ slug: entry.slug, label: entry.label, total }}
                darkMode={darkMode} radius={radius} taste={taste}
                accentColor="#D57800"
                emphasisRole={rowEmphasisRole(entry.railTitle)}
                onCategoryClick={openFilterForCategory}
              />
            ))}
            {/* ── /OCCASION RAIL ── */}

            {/* YOUR WAVE row — taste-matched events surfaced first.
                forYouEvents is hoisted above (the Von Restorff block needs
                it to know which row renders first). */}
            {forYouEvents.length > 0 && (
              <CategoryCarouselRow
                title="YOUR WAVE"
                events={forYouEvents}
                darkMode={darkMode} radius={radius} taste={taste}
                emphasisRole={rowEmphasisRole('YOUR WAVE')}
                onCategoryClick={openFilterForCategory}
              />
            )}

            {/* Category rows — user-reorderable */}
            {sectionOrder.map(cat => {
              const catEvents = chronological.filter(e => e.category === cat);
              return (
                <CategoryCarouselRow
                  key={cat}
                  title={cat}
                  events={catEvents}
                  darkMode={darkMode} radius={radius} taste={taste}
                  rowRef={el => { if (el) rowRefs.current[cat] = el; }}
                  isLifted={liftedCat === cat}
                  onHandlePointerDown={handlePointerDown}
                  onCategoryClick={openFilterForCategory}
                  emphasisRole={rowEmphasisRole(cat)}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

window.MapEventCard = MapEventCard;
window.MapView = MapView;
window.EventsMapSection = EventsMapSection;
// Self-basemap internals — unit-test surface (tests/map-basemap-self.test.js),
// the FlowsMapInternals shape.
window.MapBasemapInternals = { mapBasemapMode, MAP_GROUND_PANE, MAP_CONTEXT_BBOX_LATLNG, MAP_GROUND };
window.isFreshDaily = isFreshDaily; // shared freshness rule — index-main + gates read it
window.matchesTimeFilter = matchesTimeFilter;
window.matchesDateFilter = matchesDateFilter;
